import { ChangeDetectorRef, Component, DestroyRef, effect, OnDestroy, OnInit, signal, computed, inject } from "@angular/core";
import { TranslocoService, TranslocoPipe } from "@jsverse/transloco";
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { BasemapService } from "../../basemap/basemap.service";
import { LayerService } from "../layer.service";
import { InterfaceStatus } from "src/app/shared/contract/Enum/Status";
import { LayerGroupInfo, LayerGroupInfoFilter, LayerInfo, LayerOverviewResponse } from "src/app/shared/contract/Layer/LayerOverview";
import { InfoText } from "../../../shared/contract/InfoText/InfoText";
import { NotificationService } from "@services/notification.service";
import { PopupService } from "../../popup/popup.service";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { MapLayer } from "src/app/shared/contract/Enum/MapLayer";
import { MapContainerService } from "src/app/shared/services/map-container.service";
import { LayerApiService } from "../legend/layer-api.service";
import { MatDialogRef } from "@angular/material/dialog";
import { filter, tap } from "rxjs/operators";
import { cloneDeep } from "lodash-es";
import { OptionService } from "../../option/option.service";
import { MapLayerService } from "src/app/shared/services/map-layer.service";
import { LayerHeaderComponent } from "../layer-header/layer-header.component";
import { ExpansionPanelSkeletonComponent } from "../../skeleton-presets/expansion-panel-skeleton/expansion-panel-skeleton.component";
import { ListSkeletonComponent } from "../../skeleton-presets/list-skeleton/list-skeleton.component";
import { LayerGroupComponent } from "../layer-group/layer-group.component";
import { NavigationStart, Router } from "@angular/router";
import { FilterForm } from "../models/filter-form.interface";
import { FilterViewModel } from "../models/filter-view-model.interface";
import { LayerFormType } from "../models/layer-form-type.interface";
import { LayerGroupForm } from "../models/layer-group-form.interface";
import { LayerGroupViewModel } from "../models/layer-group-view-model.interface";
import { LayerInfoForm } from "../models/layer-info-form.interface";
import { LayerInfoViewModel } from "../models/layer-info-view-model.interface";
import { InfoTextService } from "@services/info-text.service";

@Component({
  selector: "app-layer",
  templateUrl: "./layer.component.html",
  styleUrls: ["./layer.component.scss"],
  imports: [
    LayerHeaderComponent,
    ExpansionPanelSkeletonComponent,
    ListSkeletonComponent,
    FormsModule,
    ReactiveFormsModule,
    LayerGroupComponent,
    TranslocoPipe
  ]
})
export class LayerComponent implements OnInit, OnDestroy {
  layerService = inject(LayerService);
  mapContainerService = inject(MapContainerService);

  private readonly infoTextService = inject(InfoTextService);
  private readonly mapLayerService = inject(MapLayerService);
  private readonly baseMapService = inject(BasemapService);
  private readonly translocoService = inject(TranslocoService);
  private readonly notificationService = inject(NotificationService);
  private readonly popupService = inject(PopupService);
  private readonly cdref = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly layerApiService = inject(LayerApiService);
  private readonly dialogRef = inject<MatDialogRef<LayerComponent>>(MatDialogRef);
  private readonly fb = inject(FormBuilder);
  private readonly optionService = inject(OptionService);
  private readonly router = inject(Router);

  public selectedFilterId = 1;
  isScrollable = false;
  isLoading = signal(false);
  hasError = signal(false);

  layerForm!: FormGroup<LayerFormType>;

  // ViewModel computed signal that reacts to locale changes
  layerGroupViewModels = computed<LayerGroupViewModel[]>(() => {
    // Force recomputation when language changes
    this.optionService.localeChanged();

    const layerResponse = this.layerService.layerOverviewResponse();

    return layerResponse?.layerGroup?.map(group => this.createLayerGroupViewModel(group)) ?? [];
  });

  constructor() {
    effect(() => {
      const layerOverviewResponse = this.layerService.layerOverviewResponse();
      if (layerOverviewResponse && this.layerForm) {
        this.updateFormFromLayerData();
        this.cdref.detectChanges();
      }
    });
  }

  ngOnInit() {
    this.hasError.set(false);

    this.initForm();

    if (this.layerService.layerOverviewResponse()) {
      return;
    }

    this.isLoading.set(true);

    this.layerApiService
      .getLayerOverview()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(layerOverview => {
          if (layerOverview.status === InterfaceStatus.OK && layerOverview.layerGroup) {
            this.sortLayerGroups(layerOverview.layerGroup);
            this.deactivateAllLayers(layerOverview.layerGroup);
          }
        })
      )
      .subscribe({
        next: layerOverview => {
          this.initializeLayerOverview(layerOverview);
          this.updateFormFromLayerData();
          this.isLoading.set(false);
        },
        error: () => {
          this.isLoading.set(false);
          this.hasError.set(true);
        }
      });

    this.router.events
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(event => event instanceof NavigationStart)
      )
      .subscribe((event: NavigationStart) => {
        if (event.url === "/portfolio-overview") {
          this.closeLayers();
        }
      });
  }
  ngOnDestroy() {
    this.layerService.isLayerPanelOpen.set(false);
  }

  protected closeLayers() {
    this.layerService.layerOverviewResponse.set(null);
    this.layerService.isLayerPanelOpen.set(false);

    this.popupService.closeActivePopup();
    this.mapLayerService.removeLayerByType(MapLayer.SelectedFeature);
    this.mapLayerService.removeLayerByType(MapLayer.LandUse);

    this.dialogRef.close();
  }

  protected minimizeLayers() {
    this.layerService.isLayerPanelOpen.set(false);
    this.dialogRef.close();
  }

  protected onScroll(event: Event) {
    const element = event.target as HTMLElement;
    if (!element) return;

    const isContentScrollable = this.isContentScrollable(element);
    this.isScrollable = isContentScrollable && element.scrollTop > 0;
    this.cdref.detectChanges();
  }

  protected trackByLayerGroup(index: number, control: FormGroup<LayerGroupForm>): number {
    return control.controls.id.value;
  }

  private isContentScrollable(element: HTMLElement): boolean {
    return element.scrollHeight > element.clientHeight;
  }

  // Initialize the form structure
  private initForm() {
    this.layerForm = this.fb.group<LayerFormType>({
      layerGroups: this.fb.array<FormGroup<LayerGroupForm>>([])
    });
  }

  get layerGroupsFormArray() {
    return this.layerForm.controls.layerGroups;
  }

  // Update the form with data from layerService
  private updateFormFromLayerData() {
    const layerGroups = this.layerService.layerGroups();
    if (!layerGroups?.length) {
      return;
    }

    // Clear and recreate the form array
    this.layerForm.setControl("layerGroups", this.fb.array(layerGroups.map(group => this.createLayerGroupControl(group))));

    // Set up form change listeners
    this.setupFormChangeListeners();
  }

  private createLayerGroupControl(group: LayerGroupInfo) {
    const showActivateAllOption = this.getSingleLayers(group).length >= 2;
    const hasActiveSingleLayers = this.hasActiveLayers(group);

    const activeFilter = this.getActiveFilter(group.filters);
    const filteredLayers = this.getLayersByFilter(group.layerInfos, activeFilter);

    return this.fb.group<LayerGroupForm>({
      id: this.fb.control(group.id),
      isExpanded: this.fb.control(group.isExpanded),
      layerInfos: this.fb.array(group.layerInfos.map(info => this.createLayerInfoControl(info))),
      filters: this.fb.array(group.filters.map(filter => this.createFilterControl(filter))),
      showActivateAllOption: this.fb.control(showActivateAllOption),
      hasActiveSingleLayers: this.fb.control(hasActiveSingleLayers),
      filteredLayerInfos: this.fb.control(filteredLayers)
    });
  }

  private hasActiveLayers(group: LayerGroupInfo): boolean {
    return group.layerInfos.some(info => info.isActivated);
  }

  private createLayerInfoControl(layerInfo: LayerInfo) {
    return this.fb.group<LayerInfoForm>({
      id: this.fb.control(layerInfo.id),
      isActivated: this.fb.control(layerInfo.isActivated),
      singleLayer: this.fb.control(layerInfo.singleLayer),
      filterId: this.fb.control(layerInfo.filterId),
      showInfoText: this.fb.control(layerInfo.showInfoText)
    });
  }

  private createFilterControl(filter: LayerGroupInfoFilter) {
    return this.fb.group<FilterForm>({
      id: this.fb.control(filter.id),
      isActivated: this.fb.control(filter.isActivated)
    });
  }

  // Setup listeners for form changes
  private setupFormChangeListeners() {
    for (let i = 0; i < this.layerGroupsFormArray.length; i++) {
      const group = this.layerGroupsFormArray.at(i);

      // Listen for hasActiveSingleLayers changes
      group.controls.hasActiveSingleLayers.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(isActivated => {
        const layerGroup = this.layerService.layerGroups()[i];
        if (isActivated) {
          this.activateAllSingleLayers(layerGroup);
        } else {
          this.deactivateAllSingleLayers(layerGroup);
        }
      });

      // Activate or deactivate a layer
      const layerInfosArray = group.controls.layerInfos;
      for (let j = 0; j < layerInfosArray.length; j++) {
        const layerInfo = layerInfosArray.at(j);
        layerInfo.controls.isActivated.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(isActivated => {
          const layerGroupData = this.layerService.layerGroups()[i];
          const layerInfoData = layerGroupData.layerInfos[j];
          this.handleLayerActivationChange(layerInfoData, isActivated);

          const hasActiveSingleLayers = this.areAllSingleLayersActive(layerGroupData);
          group.controls.hasActiveSingleLayers.setValue(hasActiveSingleLayers, { emitEvent: false });
        });
      }

      // Filter changes
      const filtersArray = group.controls.filters;
      for (let k = 0; k < filtersArray.length; k++) {
        const filter = filtersArray.at(k);
        filter.controls.isActivated.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(isActivated => {
          const layerGroupData = this.layerService.layerGroups()[i];
          const filterData = layerGroupData.filters[k];
          this.handleFilterChange(layerGroupData, filterData, isActivated);
        });
      }
    }
  }

  // Handle layer activation/deactivation
  private handleLayerActivationChange(activeLayerInfo: LayerInfo, isActivated: boolean) {
    const currentResponse = this.layerService.layerOverviewResponse();

    const updatedResponse = cloneDeep(currentResponse);

    let updatedLayer: LayerInfo = null;

    for (const group of updatedResponse.layerGroup) {
      for (const layerInfo of group.layerInfos) {
        if (layerInfo.id === activeLayerInfo.id) {
          // Aktualisiere den Layer
          updatedLayer = layerInfo;
          updatedLayer.isActivated = isActivated;

          // Wenn der Layer aktiviert wird und kein SingleLayer ist
          if (isActivated && !updatedLayer.singleLayer) {
            // Deaktiviere andere nicht-SingleLayers in allen Gruppen
            this.deactivateOtherLayers(updatedResponse, activeLayerInfo.id);
          }

          break;
        }
      }

      if (updatedLayer) break;
    }

    this.layerService.layerOverviewResponse.set(updatedResponse);
  }

  private deactivateOtherLayers(response: LayerOverviewResponse, activeLayerId: number): void {
    for (const group of response.layerGroup) {
      for (const layer of group.layerInfos) {
        if (layer.id !== activeLayerId && layer.isActivated && !layer.singleLayer) {
          layer.isActivated = false;
        }
      }
    }
  }

  // Handle filter changes
  private handleFilterChange(layerGroup: LayerGroupInfo, filter: LayerGroupInfoFilter, isActivated: boolean) {
    const currentResponse = this.layerService.layerOverviewResponse();
    const updatedResponse = cloneDeep(currentResponse);

    const updatedLayerGroup = updatedResponse.layerGroup.find(g => g.id === layerGroup.id);
    if (!updatedLayerGroup) return;

    const updatedFilter = updatedLayerGroup.filters.find(f => f.id === filter.id);
    if (!updatedFilter) return;

    if (isActivated) {
      this.applyFilter(updatedLayerGroup, updatedFilter);
    } else {
      updatedFilter.isActivated = false;
      updatedLayerGroup.layerInfos.forEach(layerInfo => {
        if (layerInfo.filterId === filter.id && layerInfo.isActivated) {
          layerInfo.isActivated = false;
        }
      });
    }

    this.layerService.layerOverviewResponse.set(updatedResponse);

    const filteredLayers = isActivated ? this.getLayersByFilter(updatedLayerGroup.layerInfos, updatedFilter) : [];

    const groupForm = this.layerGroupsFormArray.controls.find(g => g.value.id === layerGroup.id);
    if (groupForm) {
      groupForm.controls.filteredLayerInfos.setValue(filteredLayers);

      if (isActivated) {
        const hasActiveSingleLayersAfterFilter = this.areAllSingleLayersActive(updatedLayerGroup);
        groupForm.controls.hasActiveSingleLayers.setValue(hasActiveSingleLayersAfterFilter, { emitEvent: false });
      }
    }
  }

  // Apply a filter to a layer group
  private applyFilter(layerGroup: LayerGroupInfo, filter: LayerGroupInfoFilter) {
    // First deactivate all other filters
    layerGroup.filters.forEach(f => {
      if (f.id !== filter.id) {
        f.isActivated = false;
      }
    });

    filter.isActivated = true;

    // isLayerActivated wird automatisch berechnet
    this.popupService.closeActivePopup();

    // Remove layer from deselected subgroup
    const active = layerGroup.layerInfos.find(e => e.isActivated);
    if (active) {
      active.isActivated = false;
    }

    if (this.layerService.layerGroups().filter(e => e.layerInfos.find(f => f.isActivated)).length === 0) {
      this.layerService.setBaseLayer();
    }
  }

  // Activate all single layers in a group
  private activateAllSingleLayers(layerGroup: LayerGroupInfo) {
    const currentResponse = this.layerService.layerOverviewResponse();
    const updatedResponse = cloneDeep(currentResponse);

    // Find the layer group in the cloned response
    const updatedLayerGroup = updatedResponse.layerGroup.find(g => g.id === layerGroup.id);
    if (!updatedLayerGroup) return;

    // Activate only SingleLayers in the cloned response
    const singleLayers = updatedLayerGroup.layerInfos.filter(layer => layer.singleLayer);

    singleLayers.forEach(layerInfo => {
      layerInfo.isActivated = true;
    });

    // Set the updated response
    this.layerService.layerOverviewResponse.set(updatedResponse);
  }

  // Deactivate all single layers in a group
  private deactivateAllSingleLayers(layerGroup: LayerGroupInfo) {
    const currentResponse = this.layerService.layerOverviewResponse();
    const updatedResponse = cloneDeep(currentResponse);

    const updatedLayerGroup = updatedResponse.layerGroup.find(g => g.id === layerGroup.id);
    if (!updatedLayerGroup) return;

    const singleLayers = updatedLayerGroup.layerInfos.filter(layer => layer.singleLayer);

    singleLayers.forEach(layerInfo => {
      layerInfo.isActivated = false;
    });

    updatedLayerGroup.filters.forEach(filter => {
      filter.isActivated = false;
    });

    updatedLayerGroup.layerInfos.forEach(layerInfo => {
      if (!layerInfo.singleLayer && layerInfo.isActivated) {
        layerInfo.isActivated = false;
      }
    });

    this.layerService.layerOverviewResponse.set(updatedResponse);

    const groupForm = this.layerGroupsFormArray.controls.find(g => g.value.id === layerGroup.id);
    if (groupForm) {
      groupForm.controls.filteredLayerInfos.setValue([]);
    }
  }

  private deactivateAllLayers(groups: LayerGroupInfo[]) {
    groups.forEach(e => {
      e.isExpanded = false;
      e.layerInfos.forEach(f => {
        f.showInfoText = false;
        f.isActivated = false;
      });
    });
  }

  private sortLayerGroups(groups: LayerGroupInfo[]) {
    groups.sort((a, b) => (a.orderId > b.orderId ? 1 : -1));
    for (const e of groups) {
      e.layerInfos.sort((a, b) => (a.orderId > b.orderId ? 1 : -1));
    }
  }

  private initializeLayerOverview(layerOverviewResponse: LayerOverviewResponse) {
    if (layerOverviewResponse.status === InterfaceStatus.OK) {
      this.layerService.layerOverviewResponse.set(layerOverviewResponse);
      return;
    }

    this.handleInterfaceStatus(layerOverviewResponse.status);
    this.layerService.layerOverviewResponse.set({ layerGroup: [] });
  }

  private handleInterfaceStatus(status: InterfaceStatus) {
    if (status === InterfaceStatus.ActivePackagePromoNotInCountry) {
      this.baseMapService.country.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(country => {
        this.notificationService.failure("Exception.Failure.Status." + status, {
          Country: this.translocoService.translate("Properties.Country." + country)
        });
      });
    } else {
      this.notificationService.failure("Exception.Failure.Status." + status);
    }
  }

  private areAllSingleLayersActive(layerGroup: LayerGroupInfo): boolean {
    const singleLayers = layerGroup.layerInfos.filter(layer => layer.singleLayer);
    const activeSingleLayers = singleLayers.filter(layer => layer.isActivated);
    return singleLayers.length > 0 && singleLayers.length === activeSingleLayers.length;
  }

  private getSingleLayers(layerGroup: LayerGroupInfo): LayerInfo[] {
    return layerGroup.layerInfos.filter(layer => layer.singleLayer);
  }

  private getLayersByFilter(layers: LayerInfo[], filter?: LayerGroupInfoFilter): LayerInfo[] {
    if (!filter?.isActivated) {
      return [];
    }
    return layers.filter(layer => layer.filterId === filter.id);
  }

  private getActiveFilter(filters: LayerGroupInfoFilter[]): LayerGroupInfoFilter | undefined {
    return filters.find(filter => filter.isActivated);
  }

  private createLayerGroupViewModel(group: LayerGroupInfo): LayerGroupViewModel {
    return {
      id: group.id,
      displayName: this.getLocalizedText(group.displayName),
      isExpanded: group.isExpanded,
      layerInfos: group.layerInfos.map(layerInfo => this.createLayerInfoViewModel(layerInfo)),
      filters: group.filters.map(filter => this.createFilterViewModel(filter)),
      showActivateAllOption: this.getSingleLayers(group).length >= 2,
      hasActiveSingleLayers: this.hasActiveLayers(group),
      filteredLayerInfos: this.getFilteredLayerInfoViewModels(group),
      hasFilters: group.filters.length > 0
    };
  }

  private createLayerInfoViewModel(layerInfo: LayerInfo): LayerInfoViewModel {
    return {
      id: layerInfo.id,
      displayName: this.getLocalizedText(layerInfo.displayName),
      displayInfoText: this.getLocalizedText(layerInfo.displayInfoText),
      isActivated: layerInfo.isActivated,
      singleLayer: layerInfo.singleLayer,
      filterId: layerInfo.filterId,
      showInfoText: layerInfo.showInfoText,
      availableCountries: layerInfo.availableCountries
    };
  }

  private createFilterViewModel(filter: LayerGroupInfoFilter): FilterViewModel {
    return {
      id: filter.id,
      displayName: this.getLocalizedText(filter.displayName),
      isActivated: filter.isActivated
    };
  }

  private getFilteredLayerInfoViewModels(group: LayerGroupInfo): LayerInfoViewModel[] {
    const activeFilter = this.getActiveFilter(group.filters);
    const filteredLayers = this.getLayersByFilter(group.layerInfos, activeFilter);
    return filteredLayers.map(layerInfo => this.createLayerInfoViewModel(layerInfo));
  }

  private getLocalizedText(infoText: InfoText[]): string {
    if (!infoText) return "";
    const localizedText = this.infoTextService.getInfoTextByLocale(infoText);
    return localizedText?.[0]?.text || "";
  }

  // Method to toggle layer activation - for accessibility
  private toggleLayerActivation(layerInfoControl: FormGroup<LayerInfoForm>): void {
    const currentValue = layerInfoControl.controls.isActivated.value;
    layerInfoControl.controls.isActivated.setValue(!currentValue);
  }

  /*
   ** The following fns aren't used atm
   ** @Philipp pls delete them if you don't need them anymore
   */
  // Method to handle keyboard events for accessibility
  private onLayerKeydown(event: KeyboardEvent, layerInfoControl: FormGroup<LayerInfoForm>): void {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      this.toggleLayerActivation(layerInfoControl);
    }
  }

  private trackByLayerInfo(index: number, control: FormGroup<LayerInfoForm>): number {
    return control.controls.id.value;
  }

  private trackByFilter(index: number, control: FormGroup<FilterForm>): number {
    return control.controls.id.value;
  }

  private trackByFilteredLayerInfo(index: number, layerInfo: LayerInfoViewModel): number {
    return layerInfo.id;
  }
}
